import { useMutation } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import React, { useState, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ReactSortable } from 'react-sortablejs';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import Toaster from 'components/Toaster';
import { useGetEventForEdit } from '../EditEventV2/gql/useGetEventForEdit';
import { useTicketTemplates } from '../EditEventV2/TicketsTab/hooks/useTicketTemplates';
import { useGetAllCategories } from '../gql/queries/useGetAllCategories';
import DateSelectionModal from './components/DateSelectionModal';
import { useCreateEventTicket } from './hooks/useCreateEventTicket';
import { useDeleteEventTicket } from './hooks/useDeleteEventTicket';

// Mutation to update ticket order
const UPDATE_EVENT_TICKET_ORDER = gql`
  mutation UpdateEventTicketOrder($input: UpdateEventTicketOrderInput!) {
    updateEventTicketOrder(input: $input)
  }
`;

// ============================================================================
// SVG Icons as Components
// ============================================================================
const ChevronRightIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const ChevronDownIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const CalendarIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const LocationIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const PlusIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const EditIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const DeleteIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const DragIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 6h2v2H8V6zm6 0h2v2h-2V6zM8 11h2v2H8v-2zm6 0h2v2h-2v-2zm-6 5h2v2H8v-2zm6 0h2v2h-2v-2z" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const SingleDayIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M3 10h18" />
    <circle cx="7" cy="15" r="1.5" fill="currentColor" />
  </svg>
);

const MultiDayIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M3 10h18" />
    <path d="M9 4v18" />
    <path d="M15 4v18" />
  </svg>
);

const BundleIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="2" y="6" width="16" height="12" rx="2" />
    <rect x="6" y="4" width="16" height="12" rx="2" />
    <path d="M6 10h12" />
  </svg>
);

const OtherIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M12 18v-6" />
    <path d="M9 15h6" />
  </svg>
);

const RegistrationIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

// Platform detection
const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';

// ============================================================================
// Styled Components
// ============================================================================

// Page wrapper
const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme?.colors?.background?.primary ||
    (theme?.name === 'dark' ? '#1a1a2e' : '#f8fafc')};
  font-family: ${({ theme }) => theme?.fonts?.primary};
`;

// Page container
const Container = styled.div`
  padding: 30px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 20px 16px;
    padding-bottom: 100px;
  }
`;

// Page header
const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
`;

// Date display card - matches design system (same as EditEventV2)
const DateDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fa4616;
  color: white;
  border-radius: 8px;
  padding: 10px 16px;
  min-width: 70px;
  gap: 4px;
`;

const DatePill = styled.div`
  background: white;
  color: #1e293b;
  font-size: 14px;
  font-weight: 800;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 4px;
  letter-spacing: 0.5px;
`;

const DateText = styled.div`
  font-size: 22px;
  font-weight: 800;
  line-height: 1.2;
  text-transform: uppercase;
  white-space: nowrap;
`;

const EventInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Title = styled.h1`
  font-size: 27px;
  font-weight: 700;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
  margin: 0 0 4px 0;

  @media (max-width: 768px) {
    font-size: 23px;
  }

  @media (max-width: 480px) {
    font-size: 21px;
  }
`;

const EventMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  color: ${({ theme }) => theme?.colors?.textGray?.secondary || '#94A3B8'};
  font-size: 17px;

  span {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  svg {
    width: 18px;
    height: 18px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    font-size: 16px;
  }
`;

const HeaderButton = styled.div`
  width: auto;
  flex: ${({ fullWidth }) => (fullWidth ? 1 : 'none')};

  button {
    width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
    padding: 10px 24px;
  }

  @media (max-width: 768px) {
    flex: 1;

    button {
      width: 100%;
    }
  }
`;

// Split Layout
const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 24px;
  min-height: calc(100vh - 180px);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 20px;
    min-height: auto;
  }
`;

// Left Panel
const LeftPanel = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background || 'rgba(255, 255, 255, 0.03)'};
  border-radius: ${({ theme }) => theme?.borderRadius?.lg || '12px'};
  border: 1px solid
    ${({ theme }) => theme?.colors?.card?.border || 'rgba(255, 255, 255, 0.08)'};
  overflow: hidden;

  @media (max-width: 900px) {
    display: none;
  }
`;

const PanelTabs = styled.div`
  display: flex;
  border-bottom: 1px solid
    ${({ theme }) => theme?.colors?.border || 'rgba(255, 255, 255, 0.1)'};
`;

const PanelTab = styled.button`
  flex: 1;
  padding: 16px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme, active }) =>
    active
      ? theme?.colors?.primary || '#fa4616'
      : theme?.colors?.textGray?.muted || '#64748B'};
  cursor: pointer;
  border: none;
  background: ${({ theme, active }) =>
    active
      ? theme?.name === 'dark'
        ? 'rgba(250, 70, 22, 0.05)'
        : 'rgba(250, 70, 22, 0.05)'
      : 'none'};
  transition: all 0.2s;
  border-bottom: 2px solid
    ${({ active, theme }) =>
      active ? theme?.colors?.primary || '#fa4616' : 'transparent'};

  &:hover {
    color: ${({ theme }) => theme?.colors?.textGray?.secondary || '#94A3B8'};
    background: ${({ theme }) =>
      theme?.colors?.interactive?.hoverBackground ||
      'rgba(255, 255, 255, 0.02)'};
  }
`;

const PanelContent = styled.div`
  padding: 20px;
`;

// Ticket Type Options
const TicketTypeOption = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: ${({ theme }) =>
    theme?.colors?.card?.background || 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid
    ${({ theme }) => theme?.colors?.border || 'rgba(255, 255, 255, 0.1)'};
  border-radius: 10px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme?.colors?.primary || '#fa4616'};
    background: ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(250, 70, 22, 0.08)'
        : 'rgba(250, 70, 22, 0.05)'};
  }
`;

const TypeIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(59, 130, 246, 0.15)'
      : 'rgba(59, 130, 246, 0.1)'};
  color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#3B82F6')};
`;

const TypeInfo = styled.div`
  flex: 1;

  h3 {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 4px 0;
    color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
  }

  p {
    font-size: 13px;
    margin: 0;
    color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748B'};
  }
`;

const TypeArrow = styled.span`
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748B'};
`;

// Template List
const TemplateSearch = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.03)'};
  border: 1px solid
    ${({ theme }) => theme?.colors?.border || 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
  font-size: 14px;
  margin-bottom: 16px;

  &::placeholder {
    color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748B'};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme?.colors?.primary || '#fa4616'};
  }
`;

const TemplateList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const TemplateItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: ${({ theme }) =>
    theme?.colors?.card?.background || 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid
    ${({ theme }) => theme?.colors?.border || 'rgba(255, 255, 255, 0.08)'};
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme?.colors?.primary || '#fa4616'};
    background: ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(250, 70, 22, 0.05)'
        : 'rgba(250, 70, 22, 0.03)'};
  }

  &:hover button {
    opacity: 1;
  }
`;

const TemplateColor = styled.div`
  width: 4px;
  height: 36px;
  border-radius: 2px;
  background: ${({ color }) => color || '#3B82F6'};
`;

const TemplateDetails = styled.div`
  flex: 1;
`;

const TemplateName = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 2px;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
`;

const TemplateMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748B'};
`;

const TemplatePrice = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme?.colors?.success || '#10B981'};
`;

const TemplateAddButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) => theme?.colors?.primary || '#fa4616'};
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme?.name === 'dark' ? '#e03d10' : '#e03d10'};
  }
`;

// Right Panel
const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
`;

const TicketsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const TicketsTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
  margin: 0;

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const TicketCount = styled.span`
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748B'};
  font-size: 14px;
`;

// Category Accordion
const CategoryAccordion = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const CategorySection = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background || 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid
    ${({ theme }) => theme?.colors?.card?.border || 'rgba(255, 255, 255, 0.08)'};
  border-radius: ${({ theme }) => theme?.borderRadius?.lg || '12px'};
  margin-bottom: 16px;
  overflow: hidden;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme?.colors?.interactive?.hoverBackground ||
      'rgba(255, 255, 255, 0.03)'};
  }

  @media (max-width: 768px) {
    padding: 14px 16px;
  }
`;

const CategoryBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  color: white;
  background: ${({ color }) => color || '#3B82F6'};

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }
`;

const CategoryInfo = styled.div`
  flex: 1;
`;

const CategoryName = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 2px;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const CategorySummary = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748B'};
`;

const CategoryToggle = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)'};
  border: none;
  color: ${({ theme }) => theme?.colors?.textGray?.secondary || '#94A3B8'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  transform: ${({ expanded }) =>
    expanded ? 'rotate(180deg)' : 'rotate(0deg)'};

  &:hover {
    background: ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.08)'};
  }
`;

const CategoryBody = styled.div`
  border-top: 1px solid
    ${({ theme }) => theme?.colors?.border || 'rgba(255, 255, 255, 0.08)'};
  padding: 12px;
  display: ${({ expanded }) => (expanded ? 'block' : 'none')};

  @media (max-width: 768px) {
    padding: 8px;
  }
`;

// Design 3 - Expandable Accordion Ticket Row
const TicketCard = styled.div`
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${({ theme }) => theme?.colors?.border || 'rgba(255, 255, 255, 0.08)'};
  border-radius: 10px;
  margin-bottom: 8px;
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TicketHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : 'rgba(0, 0, 0, 0.02)'};
  }

  @media (max-width: 768px) {
    padding: 12px;
    gap: 10px;
  }
`;

const TicketDragHandle = styled.span`
  color: ${({ theme }) => theme?.colors?.textGray?.disabled || '#475569'};
  cursor: grab;
  display: flex;
  align-items: center;
  padding: 4px;

  &:hover {
    color: ${({ theme }) => theme?.colors?.textGray?.secondary || '#94A3B8'};
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const TicketIndicator = styled.div`
  width: 4px;
  height: 48px;
  border-radius: 2px;
  flex-shrink: 0;
  background: ${({ color }) => color || '#3B82F6'};

  @media (max-width: 768px) {
    height: 40px;
  }
`;

const TicketMain = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const TicketInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TicketName = styled.div`
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const TicketDates = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme?.colors?.text?.secondary || '#e2e8f0'};
  font-weight: 500;

  svg {
    color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748b'};
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const TicketMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const TicketBadge = styled.span`
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.06)'};
  color: ${({ theme }) => theme?.colors?.textGray?.secondary || '#94A3B8'};
`;

const TierTag = styled.span`
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: rgba(59, 130, 246, 0.15);
  color: #60a5fa;
`;

const WindowTag = styled.span`
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: rgba(168, 85, 247, 0.15);
  color: #a78bfa;
`;

const TicketRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const TicketPrice = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme?.colors?.success || '#10B981'};

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const TicketChevron = styled.div`
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748b'};
  transition: transform 0.2s;
  transform: ${({ expanded }) =>
    expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  display: flex;
  align-items: center;
`;

const TicketActions = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748B'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.05)'};
    color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
  }

  &.delete:hover {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

// Expanded Body
const TicketBody = styled.div`
  display: ${({ expanded }) => (expanded ? 'block' : 'none')};
  padding: 0 16px 16px 48px;
  border-top: 1px solid
    ${({ theme }) => theme?.colors?.border || 'rgba(255, 255, 255, 0.06)'};

  @media (max-width: 768px) {
    padding: 0 12px 12px 12px;
  }
`;

const TicketSection = styled.div`
  padding-top: 16px;
`;

const TicketSectionTitle = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748b'};
  margin-bottom: 10px;
`;

const TicketGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
`;

const TicketGridItem = styled.div`
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  padding: 10px 12px;
  border-radius: 6px;
`;

const TicketGridLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748b'};
  margin-bottom: 4px;
`;

const TicketGridValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.success || '#10b981'};
`;

// Bundle specific styles
const BundleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BundleItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 6px;
  border-left: 3px solid #f59e0b;
`;

const BundleQty = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #f59e0b;
  min-width: 28px;
`;

const BundleName = styled.div`
  flex: 1;
  font-size: 14px;
  color: ${({ theme }) => theme?.colors?.text?.secondary || '#e2e8f0'};
  font-weight: 500;
`;

// Mobile FAB
const MobileFab = styled.button`
  display: none;
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, #fa4616 0%, #e03d10 100%);
  border: none;
  color: white;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(250, 70, 22, 0.4);
  z-index: 100;
  align-items: center;
  justify-content: center;

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 900px) {
    display: flex;
  }
`;

// Mobile Bottom Sheet
const MobileBottomSheet = styled.div`
  display: ${({ open }) => (open ? 'block' : 'none')};
  position: fixed;
  inset: 0;
  z-index: 1000;
`;

const SheetOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
`;

const SheetContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? '#1e1e36' : '#ffffff'};
  border-radius: 20px 20px 0 0;
  max-height: 85vh;
  overflow: hidden;
  transform: ${({ open }) => (open ? 'translateY(0)' : 'translateY(100%)')};
  transition: transform 0.3s ease-out;
`;

const SheetHandle = styled.div`
  width: 40px;
  height: 4px;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)'};
  border-radius: 2px;
  margin: 12px auto;
`;

const SheetHeader = styled.div`
  padding: 8px 20px 16px;
  border-bottom: 1px solid
    ${({ theme }) => theme?.colors?.border || 'rgba(255, 255, 255, 0.1)'};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SheetTitle = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
`;

const SheetClose = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)'};
  border: none;
  color: ${({ theme }) => theme?.colors?.textGray?.secondary || '#94A3B8'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SheetBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  max-height: calc(85vh - 100px);
`;

const SheetSection = styled.div`
  margin-bottom: 24px;
`;

const SheetSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748B'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const MobileTypeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const MobileTypeCard = styled.div`
  padding: 16px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.03)'};
  border: 1px solid
    ${({ theme }) => theme?.colors?.border || 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  &:active {
    background: ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(250, 70, 22, 0.15)'
        : 'rgba(250, 70, 22, 0.1)'};
    border-color: ${({ theme }) => theme?.colors?.primary || '#fa4616'};
  }
`;

const MobileTypeIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(59, 130, 246, 0.15)'
      : 'rgba(59, 130, 246, 0.1)'};
  color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#3B82F6')};
`;

const MobileTypeName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
`;

const MobileTemplateItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.03)'};
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;

  &:active {
    background: ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(250, 70, 22, 0.15)'
        : 'rgba(250, 70, 22, 0.1)'};
  }
`;

const MobileTemplateColor = styled.div`
  width: 4px;
  height: 36px;
  border-radius: 2px;
  background: ${({ color }) => color || '#3B82F6'};
`;

const MobileTemplateInfo = styled.div`
  flex: 1;
`;

const MobileTemplateName = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 2px;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
`;

const MobileTemplateMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748B'};
`;

const MobileTemplatePrice = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme?.colors?.success || '#10B981'};
`;

// Empty State
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.03)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748B'};
`;

const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
  margin: 0 0 8px 0;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748B'};
  margin: 0 0 24px 0;
  max-width: 300px;
  line-height: 1.5;
`;

const TicketTypeHints = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 900px) {
    display: none;
  }
`;

const TypeHint = styled.button`
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.03)'};
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#8b8ba7'};
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.06)'};
    border-color: ${({ theme }) => theme?.colors?.primary || '#fa4616'};
    color: ${({ theme }) => theme?.colors?.primary || '#fa4616'};

    svg {
      stroke: ${({ theme }) => theme?.colors?.primary || '#fa4616'};
    }
  }

  svg {
    width: 16px;
    height: 16px;
    stroke: ${({ theme }) => theme?.colors?.textGray?.muted || '#6b6b8a'};
    transition: stroke 0.2s;
  }
`;

// Loading State
const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748B'};
  font-size: 14px;
`;

// Error State for templates
const TemplateErrorState = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: ${({ theme }) => theme?.colors?.error || '#ef4444'};
  font-size: 14px;
`;

const TemplateEmptyState = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748B'};
  font-size: 14px;
`;

// Confirmation Modal
const ConfirmModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const ConfirmModalContent = styled.div`
  background: ${({ theme }) =>
    theme?.name === 'dark' ? '#1a1a2e' : '#ffffff'};
  border-radius: 20px;
  padding: 32px 40px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.4);
  text-align: center;
`;

const ConfirmModalIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(239, 68, 68, 0.15)'
      : 'rgba(239, 68, 68, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #ef4444;
`;

const ConfirmModalTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
  margin: 0 0 12px 0;
`;

const ConfirmModalText = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme?.colors?.textGray?.secondary || '#94A3B8'};
  margin: 0 0 28px 0;
  line-height: 1.6;
`;

const ConfirmModalHighlight = styled.span`
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
  font-weight: 500;
`;

const ConfirmModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ConfirmModalButton = styled.button`
  padding: 12px 32px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;

  ${({ variant, theme }) =>
    variant === 'danger'
      ? `
    background: #fa4616;
    color: white;
    border: none;
    &:hover {
      background: #e03d10;
    }
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `
      : `
    background: transparent;
    color: ${theme?.colors?.text?.primary || '#ffffff'};
    border: 1px solid ${
      theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
    };
    &:hover {
      background: ${
        theme?.name === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.05)'
      };
      border-color: ${
        theme?.name === 'dark'
          ? 'rgba(255, 255, 255, 0.3)'
          : 'rgba(0, 0, 0, 0.3)'
      };
    }
  `}
`;

// Ticket Icon for empty state
const TicketIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V7z" />
    <path d="M9 12h6" />
    <path d="M12 9v6" />
  </svg>
);

// ============================================================================
// Helper Functions
// ============================================================================

// Helper to parse date for display in DateBox
const parseDateForDisplay = (startDateString, endDateString) => {
  if (!startDateString) return null;

  // Parse date - handle both MM-DD-YYYY and YYYY-MM-DD formats
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts[0].length === 4) {
      // YYYY-MM-DD
      return new Date(parts[0], parts[1] - 1, parts[2]);
    } else {
      // MM-DD-YYYY
      return new Date(parts[2], parts[0] - 1, parts[1]);
    }
  };

  const startDate = parseDate(startDateString);
  if (!startDate || isNaN(startDate.getTime())) return null;

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Check if multi-day event
  let isMultiDay = false;
  let endDay = null;

  if (endDateString && endDateString !== startDateString) {
    const endDate = parseDate(endDateString);
    if (endDate && !isNaN(endDate.getTime())) {
      isMultiDay = true;
      endDay = endDate.getDate();
    }
  }

  return {
    month: months[startDate.getMonth()],
    day: startDate.getDate(),
    dayOfWeek: days[startDate.getDay()],
    year: startDate.getFullYear(),
    isMultiDay,
    endDay,
  };
};

// Format price for display
const formatPrice = (price) => {
  const num = Number(price) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
};

// Get type label
const getTypeLabel = (type) => {
  switch (type) {
    case 'single':
      return 'Single Day';
    case 'multi':
      return 'Multi-Day';
    case 'other':
      return 'Add-On';
    case 'bundle':
      return 'Bundle';
    case 'registration':
      return 'Registration';
    default:
      return type || 'Unknown';
  }
};

// Get color for ticket type
const getTypeColor = (type) => {
  switch (type) {
    case 'single':
      return '#3B82F6';
    case 'multi':
      return '#8B5CF6';
    case 'bundle':
      return '#10B981';
    case 'other':
      return '#F59E0B';
    case 'registration':
      return '#EC4899';
    default:
      return '#3B82F6';
  }
};

// Note: Tickets will be empty for a newly created event
// This component will be enhanced later to support adding tickets via mutations

// ============================================================================
// Component
// ============================================================================
const CreateEventTicketsContent = () => {
  const history = useHistory();
  const location = useLocation();
  const { eventId } = useParams();

  // State
  const [activeTab, setActiveTab] = useState('new');
  const [templateSearch, setTemplateSearch] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [toaster, setToaster] = useState({ isOpen: false });

  // Fetch event data to get account_id and tickets
  const {
    event,
    loading: eventLoading,
    refetch: refetchEvent,
  } = useGetEventForEdit(eventId);
  const accountId = event?.account_id;

  // Fetch templates using the account_id
  const {
    templates,
    loading: templatesLoading,
    error: templatesError,
  } = useTicketTemplates(accountId);

  // Fetch all categories for grouping tickets
  const { data: categoriesData } = useGetAllCategories();
  const allCategories = categoriesData?.getAllCategories || [];

  // Mutation for creating event tickets
  const { createEventTicket, loading: isCreatingTicket } =
    useCreateEventTicket();

  // Mutation for deleting event tickets
  const { deleteEventTicket, loading: isDeletingTicket } =
    useDeleteEventTicket();

  // Mutation for updating ticket order
  const [updateTicketOrder] = useMutation(UPDATE_EVENT_TICKET_ORDER);

  // State for delete confirmation modal
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    ticket: null,
  });

  // State for expanded tickets (Design 3 accordion)
  const [expandedTickets, setExpandedTickets] = useState({});

  // Filter templates by search
  const filteredTemplates = templates.filter((t) =>
    t.name.toLowerCase().includes(templateSearch.toLowerCase())
  );

  // Extract tickets from event data - use ticket's own type field from backend
  const allTickets = useMemo(() => {
    if (!event) return [];
    return [
      ...(event.admin_tickets || []),
      ...(event.admin_multiday_tickets || []),
      ...(event.admin_other_tickets || []),
      ...(event.admin_bundle_tickets || []),
      ...(event.admin_registration_tickets || []),
    ];
  }, [event]);

  // Group tickets by category for display (all ticket types use the same categories)
  const categories = useMemo(() => {
    if (allTickets.length === 0) return [];

    // Create category map for all ticket types
    const categoryMap = {};
    allCategories.forEach((cat) => {
      const catId = String(cat.id);
      categoryMap[catId] = {
        id: cat.id,
        name: cat.name,
        tickets: [],
      };
    });

    // Add uncategorized group
    const uncategorizedGroup = {
      id: 'uncategorized',
      name: 'Uncategorized',
      tickets: [],
    };

    allTickets.forEach((ticket) => {
      // Normalize categoryId to string for consistent lookup
      const categoryId = ticket.category_id ? String(ticket.category_id) : null;
      const ticketData = {
        id: ticket.id,
        name: ticket.name,
        price: parseFloat(ticket.price) || 0,
        type: ticket.type || 'single',
        color: ticket.color_code || '#64748B',
        dates:
          ticket.start_date === ticket.end_date
            ? ticket.start_date
            : `${ticket.start_date} - ${ticket.end_date}`,
        stock: ticket.limit || 'Unlimited',
        tiers: ticket.tiers || [],
        windows: ticket.windows || [],
        associated_tickets: ticket.associated_tickets || [],
        order: ticket.order ?? 9999,
      };

      // All ticket types (including registration) use the same category lookup
      if (categoryId && categoryMap[categoryId]) {
        categoryMap[categoryId].tickets.push(ticketData);
      } else {
        uncategorizedGroup.tickets.push(ticketData);
      }
    });

    // Sort tickets within each category by order
    Object.values(categoryMap).forEach((cat) => {
      cat.tickets.sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
    });
    uncategorizedGroup.tickets.sort(
      (a, b) => (a.order ?? 9999) - (b.order ?? 9999)
    );

    // Collect categories that have tickets
    const result = [
      ...Object.values(categoryMap).filter((cat) => cat.tickets.length > 0),
    ];

    // Add uncategorized at the end if it has tickets
    if (uncategorizedGroup.tickets.length > 0) {
      result.push(uncategorizedGroup);
    }

    return result;
  }, [allTickets, allCategories]);

  const totalTickets = allTickets.length;

  // Determine route prefix for context-aware navigation
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const handleSkip = () => {
    history.push(`${routePrefix}/events/editEvent/${eventId}`);
  };

  const handleContinue = () => {
    history.push(`${routePrefix}/events/addEvent/${eventId}/finish`);
  };

  // Check if event is multi-day
  const isMultiDayEvent =
    event?.isMultiDay || event?.start_date !== event?.end_date;

  // Build ticket input from template
  // Note: color_code is assigned by the backend based on the ticket date
  const buildTicketInput = (template, startDate, endDate) => ({
    event_id: Number(eventId),
    name: template.name,
    price: template.price,
    track_price: template.track_price,
    type: template.type,
    start_date: startDate,
    end_date: endDate,
    limit: template.limit,
    description: template.description,
    description_video: template.description_video || null,
    description_image_id: template.description_image_id
      ? Number(template.description_image_id)
      : null,
    fee_dollar: template.fee_dollar,
    fee_percent: template.fee_percent,
    pos_fee_dollar: template.pos_fee_dollar,
    pos_fee_percent: template.pos_fee_percent,
    visibility: template.visibility,
    category_id: template.category_id ? Number(template.category_id) : null,
    reserved_seat: template.reserved_seat,
    reserved_seat_category: template.reserved_seat_category,
    reserved_seat_prefix: template.reserved_seat_prefix,
    reserved_seat_section: template.reserved_seat_section,
    chart_id: template.chart_id || null,
    set_member_number: template.set_member_number || false,
    prefix_text: template.prefix_text || null,
    suffix_text: template.suffix_text || null,
    last_member_number: template.last_member_number
      ? parseInt(template.last_member_number)
      : null,
    windows: template.windows?.map((w) => ({
      name: w.name,
      ends: w.ends,
      time: w.time,
      price: w.price,
      tiers: w.tiers?.map((t) => ({
        name: t.name,
        price: t.price,
        track_price: t.track_price,
      })),
    })),
    tiers: template.tiers?.map((t) => ({
      name: t.name,
      price: t.price,
      track_price: t.track_price,
    })),
    // Include pages if present (for registration tickets)
    pages:
      template.pages?.length > 0
        ? template.pages.map((p, index) => ({
            name: p.name,
            pdf_url: p.pdf_url || null,
            video_url: p.video_url || null,
            pdf_id: p.pdf_id ? Number(p.pdf_id) : null,
            form_id: p.form_id ? String(p.form_id) : null,
            waivers: (p.waivers || [])
              .filter(Boolean)
              .map((w) => ({ waiver_id: Number(w.waiver_id) })),
            order: p.order ?? index + 1,
          }))
        : null,
  });

  // Handle ticket creation from template
  const handleCreateTicketFromTemplate = async (
    template,
    startDate,
    endDate
  ) => {
    try {
      const input = buildTicketInput(template, startDate, endDate);
      const response = await createEventTicket(input);

      if (response?.data?.createEventTicket?.id) {
        setToaster({
          isOpen: true,
          type: 'success',
          title: 'Ticket Added',
          message: `"${template.name}" has been added to your event.`,
        });
        // Reset state
        setSelectedTemplate(null);
        setDateModalOpen(false);
        // Refresh ticket list
        refetchEvent();
      }
    } catch (error) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to add ticket. Please try again.',
      });
    }
  };

  const handleAddTicket = (type) => {
    setSheetOpen(false);
    // Navigate to the AddEventTicket page with optional type pre-selected
    const basePath = `${routePrefix}/events/editEvent/${eventId}/tickets/add`;
    history.push({
      pathname: basePath,
      state: { ticketType: type, fromWizard: true },
    });
  };

  const handleSelectTemplate = (template) => {
    setSheetOpen(false);

    if (isMultiDayEvent) {
      // Multi-day event: open modal to select dates
      setSelectedTemplate(template);
      setDateModalOpen(true);
    } else {
      // Single-day event: create ticket directly with event's start date
      handleCreateTicketFromTemplate(
        template,
        event.start_date,
        event.start_date
      );
    }
  };

  const handleDateModalSubmit = ({ startDate, endDate }) => {
    if (selectedTemplate) {
      handleCreateTicketFromTemplate(selectedTemplate, startDate, endDate);
    }
  };

  const handleDateModalClose = () => {
    setDateModalOpen(false);
    setSelectedTemplate(null);
  };

  // Handle edit ticket - navigate to edit form
  const handleEditTicket = (ticket) => {
    history.push({
      pathname: `${routePrefix}/events/editEvent/${eventId}/tickets/${ticket.id}/edit`,
      state: { fromWizard: true },
    });
  };

  // Handle delete ticket - open confirmation modal
  const handleDeleteClick = (ticket) => {
    setDeleteModal({ isOpen: true, ticket });
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteModal.ticket) return;

    try {
      const response = await deleteEventTicket(deleteModal.ticket.id);

      if (response?.data?.deleteEventTicket) {
        setToaster({
          isOpen: true,
          type: 'success',
          title: 'Ticket Deleted',
          message: `"${deleteModal.ticket.name}" has been deleted.`,
        });
        // Refetch event data to update ticket list
        refetchEvent();
      }
    } catch (error) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to delete ticket. Please try again.',
      });
    } finally {
      setDeleteModal({ isOpen: false, ticket: null });
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, ticket: null });
  };

  // Handle ticket reorder within a category
  const handleTicketReorder = async (
    _categoryId,
    newTicketOrder,
    originalTickets
  ) => {
    // Only proceed if this is a reorder (same length, different order)
    if (newTicketOrder.length !== originalTickets.length) {
      return; // Item was added or removed, not a reorder
    }

    // Check if order actually changed
    const orderChanged = newTicketOrder.some(
      (ticket, index) => ticket.id !== originalTickets[index]?.id
    );

    if (!orderChanged) {
      return; // Order didn't change
    }

    // Build the tickets array with new order values
    const ticketsWithOrder = newTicketOrder.map((ticket, index) => ({
      id: parseInt(ticket.id),
      order: index + 1,
    }));

    try {
      await updateTicketOrder({
        variables: {
          input: {
            event_id: parseInt(eventId),
            tickets: ticketsWithOrder,
          },
        },
      });
      // Refetch to get updated order from server
      refetchEvent();
    } catch (error) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to update ticket order.',
      });
    }
  };

  // Toggle ticket expansion
  const toggleTicketExpanded = (ticketId) => {
    setExpandedTickets((prev) => ({
      ...prev,
      [ticketId]: !prev[ticketId],
    }));
  };

  return (
    <PageWrapper>
      <Container>
        <PageHeader>
          <HeaderLeft>
            {(() => {
              const dateInfo = parseDateForDisplay(
                event?.start_date,
                event?.end_date
              );
              if (!dateInfo) return null;
              return (
                <DateDisplay>
                  {dateInfo.isMultiDay ? (
                    <>
                      <DatePill>{dateInfo.month}</DatePill>
                      <DateText>
                        {dateInfo.day} - {dateInfo.endDay}
                      </DateText>
                    </>
                  ) : (
                    <>
                      <DatePill>{dateInfo.dayOfWeek}</DatePill>
                      <DateText>
                        {dateInfo.month} {dateInfo.day}
                      </DateText>
                    </>
                  )}
                </DateDisplay>
              );
            })()}
            <EventInfo>
              <Title>{event?.name || 'Event Tickets'}</Title>
              <EventMeta>
                {event?.track?.name && (
                  <span>
                    <LocationIcon />
                    {event.track.name}
                  </span>
                )}
              </EventMeta>
            </EventInfo>
          </HeaderLeft>
          <HeaderRight>
            <HeaderButton>
              <ButtonV2 type="button" onClick={handleSkip}>
                Skip for Now
              </ButtonV2>
            </HeaderButton>
            <HeaderButton>
              <ButtonV2 elevated type="button" onClick={handleContinue}>
                Continue
              </ButtonV2>
            </HeaderButton>
          </HeaderRight>
        </PageHeader>

        <SplitLayout>
          {/* Left Panel - Desktop only */}
          <LeftPanel>
            <PanelTabs>
              <PanelTab
                active={activeTab === 'new'}
                onClick={() => setActiveTab('new')}
              >
                New Ticket
              </PanelTab>
              <PanelTab
                active={activeTab === 'template'}
                onClick={() => setActiveTab('template')}
              >
                From Template
              </PanelTab>
            </PanelTabs>

            <PanelContent>
              {activeTab === 'new' ? (
                <>
                  <TicketTypeOption onClick={() => handleAddTicket('single')}>
                    <TypeIcon>
                      <SingleDayIcon />
                    </TypeIcon>
                    <TypeInfo>
                      <h3>Single Day Ticket</h3>
                      <p>Valid for one day of the event</p>
                    </TypeInfo>
                    <TypeArrow>
                      <ChevronRightIcon />
                    </TypeArrow>
                  </TicketTypeOption>

                  {isMultiDayEvent && !isTicketHoss && (
                    <TicketTypeOption onClick={() => handleAddTicket('multi')}>
                      <TypeIcon>
                        <MultiDayIcon />
                      </TypeIcon>
                      <TypeInfo>
                        <h3>Multi-Day Pass</h3>
                        <p>Access for multiple event days</p>
                      </TypeInfo>
                      <TypeArrow>
                        <ChevronRightIcon />
                      </TypeArrow>
                    </TicketTypeOption>
                  )}

                  {isTicketHoss ? (
                    <TicketTypeOption onClick={() => handleAddTicket('bundle')}>
                      <TypeIcon>
                        <BundleIcon />
                      </TypeIcon>
                      <TypeInfo>
                        <h3>Bundle Package</h3>
                        <p>Combine multiple tickets together</p>
                      </TypeInfo>
                      <TypeArrow>
                        <ChevronRightIcon />
                      </TypeArrow>
                    </TicketTypeOption>
                  ) : (
                    <TicketTypeOption
                      onClick={() => handleAddTicket('registration')}
                    >
                      <TypeIcon>
                        <RegistrationIcon />
                      </TypeIcon>
                      <TypeInfo>
                        <h3>Registration</h3>
                        <p>Registration form for participants</p>
                      </TypeInfo>
                      <TypeArrow>
                        <ChevronRightIcon />
                      </TypeArrow>
                    </TicketTypeOption>
                  )}

                  <TicketTypeOption onClick={() => handleAddTicket('other')}>
                    <TypeIcon>
                      <OtherIcon />
                    </TypeIcon>
                    <TypeInfo>
                      <h3>Add-On</h3>
                      <p>Non-entry items like parking, merch, camping</p>
                    </TypeInfo>
                    <TypeArrow>
                      <ChevronRightIcon />
                    </TypeArrow>
                  </TicketTypeOption>
                </>
              ) : (
                <>
                  <TemplateSearch
                    type="text"
                    placeholder="Search templates..."
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                  />
                  <TemplateList>
                    {templatesLoading && (
                      <LoadingState>Loading templates...</LoadingState>
                    )}

                    {!templatesLoading && templatesError && (
                      <TemplateErrorState>
                        Failed to load templates. Please try again.
                      </TemplateErrorState>
                    )}

                    {!templatesLoading &&
                      !templatesError &&
                      filteredTemplates.length === 0 && (
                        <TemplateEmptyState>
                          {templateSearch
                            ? 'No templates match your search.'
                            : 'No templates available for this account.'}
                        </TemplateEmptyState>
                      )}

                    {!templatesLoading &&
                      !templatesError &&
                      filteredTemplates.map((template) => (
                        <TemplateItem
                          key={template.id}
                          onClick={() => handleSelectTemplate(template)}
                        >
                          <TemplateColor color={getTypeColor(template.type)} />
                          <TemplateDetails>
                            <TemplateName>{template.name}</TemplateName>
                            <TemplateMeta>
                              {getTypeLabel(template.type)} &bull;{' '}
                              {formatPrice(template.price)}
                            </TemplateMeta>
                          </TemplateDetails>
                          <TemplatePrice>
                            {formatPrice(template.price)}
                          </TemplatePrice>
                          <TemplateAddButton>
                            <PlusIcon size={16} />
                          </TemplateAddButton>
                        </TemplateItem>
                      ))}
                  </TemplateList>
                </>
              )}
            </PanelContent>
          </LeftPanel>

          {/* Right Panel - Ticket List */}
          <RightPanel>
            {eventLoading ? (
              <LoadingState>Loading event data...</LoadingState>
            ) : totalTickets === 0 ? (
              /* Empty State */
              <EmptyState>
                <EmptyStateIcon>
                  <TicketIcon />
                </EmptyStateIcon>
                <EmptyStateTitle>No tickets yet</EmptyStateTitle>
                <EmptyStateText>
                  Choose a ticket type from the panel to get started.
                </EmptyStateText>
                <TicketTypeHints>
                  <TypeHint onClick={() => handleAddTicket('single')}>
                    <SingleDayIcon />
                    Single Day
                  </TypeHint>
                  {isMultiDayEvent && !isTicketHoss && (
                    <TypeHint onClick={() => handleAddTicket('multi')}>
                      <MultiDayIcon />
                      Multi-Day
                    </TypeHint>
                  )}
                  {isTicketHoss ? (
                    <TypeHint onClick={() => handleAddTicket('bundle')}>
                      <BundleIcon />
                      Bundle
                    </TypeHint>
                  ) : (
                    <TypeHint onClick={() => handleAddTicket('registration')}>
                      <RegistrationIcon />
                      Registration
                    </TypeHint>
                  )}
                  <TypeHint onClick={() => handleAddTicket('other')}>
                    <OtherIcon />
                    Add-On
                  </TypeHint>
                </TicketTypeHints>
              </EmptyState>
            ) : (
              /* Ticket List */
              <>
                <TicketsHeader>
                  <TicketsTitle>Your Tickets</TicketsTitle>
                  <TicketCount>
                    {totalTickets} ticket{totalTickets !== 1 ? 's' : ''} in{' '}
                    {categories.length} categor
                    {categories.length !== 1 ? 'ies' : 'y'}
                  </TicketCount>
                </TicketsHeader>

                <CategoryAccordion>
                  {categories.map((category) => {
                    const totalValue = category.tickets.reduce(
                      (sum, t) => sum + t.price,
                      0
                    );

                    return (
                      <CategorySection key={category.id}>
                        <CategoryHeader>
                          <CategoryBadge color="#64748B">
                            {category.name.charAt(0)}
                          </CategoryBadge>
                          <CategoryInfo>
                            <CategoryName>{category.name}</CategoryName>
                            <CategorySummary>
                              {category.tickets.length} ticket
                              {category.tickets.length !== 1
                                ? 's'
                                : ''} &bull; {formatPrice(totalValue)} total
                              value
                            </CategorySummary>
                          </CategoryInfo>
                          <CategoryToggle expanded={true}>
                            <ChevronDownIcon />
                          </CategoryToggle>
                        </CategoryHeader>
                        <CategoryBody expanded={true}>
                          <ReactSortable
                            list={category.tickets}
                            setList={(newOrder) =>
                              handleTicketReorder(
                                category.id,
                                newOrder,
                                category.tickets
                              )
                            }
                            animation={200}
                            handle=".drag-handle"
                          >
                            {category.tickets.map((ticket) => {
                              const isBundle = ticket.type === 'bundle';
                              const tierCount = ticket.tiers?.length || 0;
                              const windowCount = ticket.windows?.length || 0;
                              const bundleTicketCount =
                                ticket.associated_tickets?.length || 0;
                              const hasExpandableContent = isBundle
                                ? bundleTicketCount > 0
                                : tierCount > 0 || windowCount > 0;
                              const isExpanded =
                                hasExpandableContent &&
                                (expandedTickets[ticket.id] || false);

                              return (
                                <TicketCard key={ticket.id}>
                                  <TicketHeader
                                    onClick={() =>
                                      hasExpandableContent &&
                                      toggleTicketExpanded(ticket.id)
                                    }
                                    style={{
                                      cursor: hasExpandableContent
                                        ? 'pointer'
                                        : 'default',
                                    }}
                                  >
                                    <TicketDragHandle
                                      className="drag-handle"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <DragIcon />
                                    </TicketDragHandle>
                                    <TicketIndicator color={ticket.color} />
                                    <TicketMain>
                                      <TicketInfo>
                                        <TicketName>{ticket.name}</TicketName>
                                        <TicketDates>
                                          <CalendarIcon />
                                          {ticket.dates}
                                        </TicketDates>
                                        <TicketMeta>
                                          <TicketBadge>
                                            {getTypeLabel(ticket.type)}
                                          </TicketBadge>
                                        </TicketMeta>
                                      </TicketInfo>
                                    </TicketMain>
                                    <TicketRight>
                                      {isBundle && bundleTicketCount > 0 && (
                                        <TierTag>
                                          {bundleTicketCount} Ticket
                                          {bundleTicketCount !== 1 ? 's' : ''}
                                        </TierTag>
                                      )}
                                      {!isBundle && tierCount > 0 && (
                                        <TierTag>
                                          {tierCount} Tier
                                          {tierCount !== 1 ? 's' : ''}
                                        </TierTag>
                                      )}
                                      {!isBundle && windowCount > 0 && (
                                        <WindowTag>
                                          {windowCount} Window
                                          {windowCount !== 1 ? 's' : ''}
                                        </WindowTag>
                                      )}
                                      <TicketPrice>
                                        {formatPrice(ticket.price)}
                                      </TicketPrice>
                                      {hasExpandableContent && (
                                        <TicketChevron expanded={isExpanded}>
                                          <ChevronDownIcon size={20} />
                                        </TicketChevron>
                                      )}
                                    </TicketRight>
                                    <TicketActions
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <ActionButton
                                        onClick={() => handleEditTicket(ticket)}
                                      >
                                        <EditIcon />
                                      </ActionButton>
                                      <ActionButton
                                        className="delete"
                                        onClick={() =>
                                          handleDeleteClick(ticket)
                                        }
                                      >
                                        <DeleteIcon />
                                      </ActionButton>
                                    </TicketActions>
                                  </TicketHeader>

                                  {hasExpandableContent && (
                                    <TicketBody expanded={isExpanded}>
                                      {isBundle && bundleTicketCount > 0 ? (
                                        <TicketSection>
                                          <TicketSectionTitle>
                                            Included Tickets
                                          </TicketSectionTitle>
                                          <BundleList>
                                            {ticket.associated_tickets.map(
                                              (assocTicket, idx) => (
                                                <BundleItem
                                                  key={assocTicket.id || idx}
                                                >
                                                  <BundleQty>
                                                    {assocTicket.quantity}x
                                                  </BundleQty>
                                                  <BundleName>
                                                    {assocTicket.name}
                                                  </BundleName>
                                                </BundleItem>
                                              )
                                            )}
                                          </BundleList>
                                        </TicketSection>
                                      ) : (
                                        <>
                                          {tierCount > 0 && (
                                            <TicketSection>
                                              <TicketSectionTitle>
                                                Pricing Tiers
                                              </TicketSectionTitle>
                                              <TicketGrid>
                                                {ticket.tiers.map(
                                                  (tier, idx) => (
                                                    <TicketGridItem
                                                      key={tier.id || idx}
                                                    >
                                                      <TicketGridLabel>
                                                        {tier.name}
                                                      </TicketGridLabel>
                                                      <TicketGridValue>
                                                        {formatPrice(
                                                          tier.price
                                                        )}
                                                      </TicketGridValue>
                                                    </TicketGridItem>
                                                  )
                                                )}
                                              </TicketGrid>
                                            </TicketSection>
                                          )}
                                          {windowCount > 0 && (
                                            <TicketSection>
                                              <TicketSectionTitle>
                                                Sales Windows
                                              </TicketSectionTitle>
                                              <TicketGrid>
                                                {ticket.windows.map(
                                                  (window, idx) => (
                                                    <TicketGridItem
                                                      key={window.id || idx}
                                                    >
                                                      <TicketGridLabel>
                                                        {window.name}
                                                      </TicketGridLabel>
                                                      <TicketGridValue>
                                                        {formatPrice(
                                                          window.price
                                                        )}
                                                      </TicketGridValue>
                                                    </TicketGridItem>
                                                  )
                                                )}
                                              </TicketGrid>
                                            </TicketSection>
                                          )}
                                        </>
                                      )}
                                    </TicketBody>
                                  )}
                                </TicketCard>
                              );
                            })}
                          </ReactSortable>
                        </CategoryBody>
                      </CategorySection>
                    );
                  })}
                </CategoryAccordion>
              </>
            )}
          </RightPanel>
        </SplitLayout>
      </Container>

      {/* Mobile FAB */}
      <MobileFab onClick={() => setSheetOpen(true)}>
        <PlusIcon size={24} />
      </MobileFab>

      {/* Mobile Bottom Sheet */}
      <MobileBottomSheet open={sheetOpen}>
        <SheetOverlay onClick={() => setSheetOpen(false)} />
        <SheetContent open={sheetOpen}>
          <SheetHandle />
          <SheetHeader>
            <SheetTitle>Add Ticket</SheetTitle>
            <SheetClose onClick={() => setSheetOpen(false)}>
              <CloseIcon />
            </SheetClose>
          </SheetHeader>
          <SheetBody>
            <SheetSection>
              <SheetSectionTitle>Create New</SheetSectionTitle>
              <MobileTypeGrid>
                <MobileTypeCard onClick={() => handleAddTicket('single')}>
                  <MobileTypeIcon>
                    <SingleDayIcon />
                  </MobileTypeIcon>
                  <MobileTypeName>Single Day</MobileTypeName>
                </MobileTypeCard>
                {isMultiDayEvent && !isTicketHoss && (
                  <MobileTypeCard onClick={() => handleAddTicket('multi')}>
                    <MobileTypeIcon>
                      <MultiDayIcon />
                    </MobileTypeIcon>
                    <MobileTypeName>Multi-Day</MobileTypeName>
                  </MobileTypeCard>
                )}
                {isTicketHoss ? (
                  <MobileTypeCard onClick={() => handleAddTicket('bundle')}>
                    <MobileTypeIcon>
                      <BundleIcon />
                    </MobileTypeIcon>
                    <MobileTypeName>Bundle</MobileTypeName>
                  </MobileTypeCard>
                ) : (
                  <MobileTypeCard
                    onClick={() => handleAddTicket('registration')}
                  >
                    <MobileTypeIcon>
                      <RegistrationIcon />
                    </MobileTypeIcon>
                    <MobileTypeName>Registration</MobileTypeName>
                  </MobileTypeCard>
                )}
                <MobileTypeCard onClick={() => handleAddTicket('other')}>
                  <MobileTypeIcon>
                    <OtherIcon />
                  </MobileTypeIcon>
                  <MobileTypeName>Add-On</MobileTypeName>
                </MobileTypeCard>
              </MobileTypeGrid>
            </SheetSection>
            <SheetSection>
              <SheetSectionTitle>From Template</SheetSectionTitle>
              {templatesLoading && (
                <LoadingState>Loading templates...</LoadingState>
              )}

              {!templatesLoading && templatesError && (
                <TemplateErrorState>
                  Failed to load templates.
                </TemplateErrorState>
              )}

              {!templatesLoading &&
                !templatesError &&
                templates.length === 0 && (
                  <TemplateEmptyState>
                    No templates available for this account.
                  </TemplateEmptyState>
                )}

              {!templatesLoading &&
                !templatesError &&
                templates.slice(0, 5).map((template) => (
                  <MobileTemplateItem
                    key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                  >
                    <MobileTemplateColor color={getTypeColor(template.type)} />
                    <MobileTemplateInfo>
                      <MobileTemplateName>{template.name}</MobileTemplateName>
                      <MobileTemplateMeta>
                        {getTypeLabel(template.type)} &bull;{' '}
                        {formatPrice(template.price)}
                      </MobileTemplateMeta>
                    </MobileTemplateInfo>
                    <MobileTemplatePrice>
                      {formatPrice(template.price)}
                    </MobileTemplatePrice>
                  </MobileTemplateItem>
                ))}
            </SheetSection>
          </SheetBody>
        </SheetContent>
      </MobileBottomSheet>

      {/* Date Selection Modal for Multi-Day Events */}
      <DateSelectionModal
        isVisible={dateModalOpen}
        onClose={handleDateModalClose}
        onSubmit={handleDateModalSubmit}
        template={selectedTemplate}
        event={event}
        isSubmitting={isCreatingTicket}
      />

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <ConfirmModalOverlay onClick={handleDeleteCancel}>
          <ConfirmModalContent onClick={(e) => e.stopPropagation()}>
            <ConfirmModalIcon>
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </ConfirmModalIcon>
            <ConfirmModalTitle>Delete Ticket?</ConfirmModalTitle>
            <ConfirmModalText>
              Are you sure you want to delete{' '}
              <ConfirmModalHighlight>
                {deleteModal.ticket?.name}
              </ConfirmModalHighlight>
              ?
              <br />
              This action cannot be undone.
            </ConfirmModalText>
            <ConfirmModalActions>
              <ConfirmModalButton onClick={handleDeleteCancel}>
                Cancel
              </ConfirmModalButton>
              <ConfirmModalButton
                variant="danger"
                onClick={handleDeleteConfirm}
                disabled={isDeletingTicket}
              >
                {isDeletingTicket ? 'Deleting...' : 'Delete'}
              </ConfirmModalButton>
            </ConfirmModalActions>
          </ConfirmModalContent>
        </ConfirmModalOverlay>
      )}

      {/* Toast Notifications */}
      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
      />
    </PageWrapper>
  );
};

// Main component wrapped with theme provider
const CreateEventTickets = () => {
  return (
    <EnhancedThemeProvider>
      <CreateEventTicketsContent />
    </EnhancedThemeProvider>
  );
};

export default CreateEventTickets;
